import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Language,
  Send,
  DataObject,
  ExpandMore,
  Public,
} from '@mui/icons-material';
import { externalApiService } from '../services/api';

interface ApiResponse {
  data: any;
  status: number;
  statusText: string;
  timestamp: string;
}

const ExternalPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [apiResponse, setApiResponse] = useState<ApiResponse | null>(null);
  const [customUrl, setCustomUrl] = useState('');

  const handleApiCall = async (endpoint: string, description: string) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      let response;
      switch (endpoint) {
        case 'posts':
          response = await externalApiService.getPosts();
          break;
        case 'users':
          response = await externalApiService.getUsers();
          break;
        case 'comments':
          response = await externalApiService.getComments();
          break;
        case 'custom':
          if (!customUrl.trim()) {
            setError('Please enter a valid URL');
            return;
          }
          response = await externalApiService.getCustomData(customUrl);
          break;
        default:
          setError('Unknown endpoint');
          return;
      }

      setApiResponse({
        data: response.data,
        status: response.status,
        statusText: response.statusText,
        timestamp: new Date().toISOString(),
      });
      
      setSuccess(`Successfully fetched data from ${description}`);
    } catch (err: any) {
      setError(`Failed to fetch data: ${err.message || 'Unknown error'}`);
      console.error('API call error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatJson = (obj: any): string => {
    return JSON.stringify(obj, null, 2);
  };

  const predefinedEndpoints = [
    {
      id: 'posts',
      title: 'JSONPlaceholder Posts',
      description: 'Fetch sample blog posts from JSONPlaceholder API',
      url: 'https://jsonplaceholder.typicode.com/posts',
      icon: <DataObject />,
    },
    {
      id: 'users',
      title: 'JSONPlaceholder Users',
      description: 'Fetch sample users from JSONPlaceholder API',
      url: 'https://jsonplaceholder.typicode.com/users',
      icon: <Public />,
    },
    {
      id: 'comments',
      title: 'JSONPlaceholder Comments',
      description: 'Fetch sample comments from JSONPlaceholder API',
      url: 'https://jsonplaceholder.typicode.com/comments',
      icon: <Language />,
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 'bold' }}>
          External API Integration
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Test external API calls and integrate with third-party services.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 3 }}>
            Predefined API Endpoints
          </Typography>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, 
            gap: 2 
          }}>
            {predefinedEndpoints.map((endpoint) => (
              <Card key={endpoint.id} variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {endpoint.icon}
                    <Typography variant="h6" component="h3" sx={{ ml: 1 }}>
                      {endpoint.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {endpoint.description}
                  </Typography>
                  <Chip label={endpoint.url} size="small" variant="outlined" />
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleApiCall(endpoint.id, endpoint.title)}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : <Send />}
                    fullWidth
                  >
                    {loading ? 'Loading...' : 'Test API'}
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 3 }}>
            Custom API Endpoint
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              label="Custom API URL"
              placeholder="https://api.example.com/endpoint"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              fullWidth
              variant="outlined"
              helperText="Enter any public API URL to test"
            />
            <Button
              variant="contained"
              onClick={() => handleApiCall('custom', 'Custom Endpoint')}
              disabled={loading || !customUrl.trim()}
              startIcon={loading ? <CircularProgress size={16} /> : <Send />}
              sx={{ minWidth: 120, height: 56 }}
            >
              {loading ? 'Testing...' : 'Test'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {apiResponse && (
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 3 }}>
              API Response
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Chip label={`Status: ${apiResponse.status} ${apiResponse.statusText}`} color="success" size="small" />
              <Chip label={`Time: ${new Date(apiResponse.timestamp).toLocaleTimeString()}`} size="small" sx={{ ml: 1 }} />
            </Box>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Response Data</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <pre style={{ 
                    margin: 0, 
                    fontSize: '0.875rem', 
                    overflow: 'auto',
                    maxHeight: '400px'
                  }}>
                    {formatJson(apiResponse.data)}
                  </pre>
                </Paper>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      )}

      {loading && !apiResponse && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Fetching data from external API...
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default ExternalPage;